import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { twMerge } from 'tailwind-merge';
import SumsubIcon from '../../assets/svg/sumsub.svg';
import { AuthStateType, useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { getVerificationFlags } from '../../lib/verificationStatus';
import type { SumsubVerificationConfig } from './types';
import { useKybStatusRefresh } from './useKybStatusRefresh';
import { useSumsubSdkLifecycle } from './useSumsubSdkLifecycle';

export type { SumsubVerificationConfig } from './types';

interface Props {
  className?: string;
  config: SumsubVerificationConfig;
}

function getVerifyHeading(type: 'business' | 'identity') {
  return type === 'business'
    ? 'Verify your business'
    : 'Verify your personal identity';
}

function getInProgressText(type: 'business' | 'identity', canResume: boolean) {
  const label = type;
  return canResume
    ? `Your ${label} verification is in progress. If you haven't finished uploading documents, you can continue below.`
    : `Your ${label} verification is in progress and is currently being processed.`;
}

function getRejectedText(type: 'business' | 'identity') {
  const label = type;
  return `Your ${label} verification was rejected. Please contact support for assistance.`;
}

function getDefaultText(type: 'business' | 'identity', hasApplicant: boolean) {
  const label = type;
  return hasApplicant
    ? 'You started verification but did not finish submitting your documents. Continue below to resume.'
    : `To submit dApps and complete regulatory compliance, click below to begin the ${label} verification process with our partner, SumSub.`;
}

export function SumsubVerificationPage({ className, config }: Props) {
  const trpc = useTRPC();
  const [authState] = useAuth();
  const {
    activePublisher,
    isLoading: isPublisherLoading,
    refresh: refreshPublisher,
  } = usePublisher();
  const navigate = useNavigate();
  const sdkContainerRef = useRef<HTMLDivElement>(null);

  usePageTitle(config.pageTitle);

  useEffect(() => {
    if (authState.type === AuthStateType.Authenticated) {
      if (isPublisherLoading) {
        return;
      }
      if (!activePublisher) {
        navigate('/publisher-access', { replace: true });
        return;
      }
      if (config.shouldRedirect(activePublisher?.publisherType)) {
        navigate(config.redirectPath, { replace: true });
      }
    }
  }, [
    activePublisher?.publisherType,
    authState,
    config,
    isPublisherLoading,
    navigate,
  ]);

  const { kybStatus, setKybStatus, statusLoading, refreshKybStatus } =
    useKybStatusRefresh({
      trpc,
      activePublisherJoinId: activePublisher?.publisherUserJoinId,
      hasActivePublisher: Boolean(activePublisher),
      isPublisherLoading,
    });

  const verificationFlags = getVerificationFlags(kybStatus);
  const {
    hasApplicant,
    isInProgress,
    isUnderReview,
    isApproved,
    isRejected,
    needsAdditionalInfo,
    canResume,
  } = verificationFlags;
  const kybSubmissionId = kybStatus?.kybSubmissionId;

  const {
    error,
    notice,
    loading,
    showSdk,
    initializeSumsubSdk,
    restartVerification,
  } = useSumsubSdkLifecycle({
    trpc,
    config,
    kybSubmissionId,
    canResume,
    setKybStatus,
    refreshKybStatus,
    refreshPublisher,
    navigate,
    sdkContainerRef,
    activePublisherJoinId: activePublisher?.publisherUserJoinId,
    isPublisherLoading,
  });

  const isVerificationInProgress = isInProgress || isUnderReview || isApproved;
  const isVerificationRejected = isRejected;

  const statusMessage = error ? (
    <div className="mt-4 text-sm text-red-400 text-center">{error}</div>
  ) : notice ? (
    <div className="mt-4 text-sm text-yellow-300 text-center">{notice}</div>
  ) : null;

  return (
    <div
      className={twMerge('flex-1 flex items-center justify-center', className)}
    >
      <div className="w-full max-w-lg px-6">
        {statusLoading ? (
          <div className="text-center">
            <div className="text-sm text-neutral-400">
              Loading verification status…
            </div>
          </div>
        ) : !showSdk ? (
          <div className="text-center">
            <div className="mb-6 flex justify-center">
              <div className="size-[72px] bg-white rounded-2xl flex items-center justify-center">
                <img src={SumsubIcon} alt="Sumsub" className="size-10" />
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-white mb-4">
              {isVerificationInProgress
                ? 'Verification Status'
                : isVerificationRejected
                  ? 'Verification Rejected'
                  : needsAdditionalInfo
                    ? 'Additional Information Required'
                    : getVerifyHeading(config.verificationType)}
            </h1>

            {isVerificationInProgress ? (
              <>
                <p className="text-sm text-neutral-400 mb-8">
                  {getInProgressText(config.verificationType, canResume)}
                </p>
                <div className="bg-[#1C2127] border border-[#2A2F36] rounded-xl p-6 text-left">
                  <div className="mb-4">
                    <div className="text-xs text-neutral-500 mb-1">Status</div>
                    <div className="text-sm text-white font-medium">
                      {kybStatus?.status === 'InProgress' &&
                        'Verification In Progress'}
                      {kybStatus?.status === 'UnderReview' && 'Under Review'}
                      {kybStatus?.status === 'Approved' && 'Approved'}
                    </div>
                  </div>
                  {kybStatus?.submittedAt && (
                    <div className="mb-4">
                      <div className="text-xs text-neutral-500 mb-1">
                        Submitted At
                      </div>
                      <div className="text-sm text-white">
                        {new Date(kybStatus.submittedAt).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {kybStatus?.reviewStartedAt && (
                    <div>
                      <div className="text-xs text-neutral-500 mb-1">
                        Review Started At
                      </div>
                      <div className="text-sm text-white">
                        {new Date(kybStatus.reviewStartedAt).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
                {canResume && (
                  <div className="mt-6 flex flex-col gap-3">
                    <p className="text-xs text-neutral-500">
                      If you haven&apos;t finished uploading your documents, you
                      can continue your verification below.
                    </p>
                    <button
                      type="button"
                      className={twMerge(
                        'bg-[#34FFC2]',
                        'flex',
                        'h-12',
                        'items-center',
                        'justify-center',
                        'rounded-xl',
                        'w-full',
                        'hover:bg-[#2FEDB3]',
                        'transition-colors',
                        'font-medium',
                        loading && 'opacity-50 cursor-not-allowed',
                      )}
                      onClick={() => initializeSumsubSdk()}
                      disabled={loading}
                    >
                      <div className="text-sm text-[#002365]">
                        {loading ? 'Loading...' : 'Continue Verification'}
                      </div>
                    </button>
                    {kybStatus?.status === 'NotStarted' && hasApplicant && (
                      <>
                        <p className="text-xs text-neutral-500">
                          If Sumsub says the verification expired or opens the
                          wrong flow, start a new verification instead.
                        </p>
                        <button
                          type="button"
                          className={twMerge(
                            'border border-neutral-700 text-neutral-200',
                            'flex',
                            'h-12',
                            'items-center',
                            'justify-center',
                            'rounded-xl',
                            'w-full',
                            'hover:border-neutral-500 hover:text-white',
                            'transition-colors',
                            loading && 'opacity-50 cursor-not-allowed',
                          )}
                          onClick={restartVerification}
                          disabled={loading}
                        >
                          <div className="text-sm">
                            {loading ? 'Loading...' : 'Start New Verification'}
                          </div>
                        </button>
                      </>
                    )}
                    {statusMessage}
                  </div>
                )}
                {!canResume && statusMessage}
              </>
            ) : isVerificationRejected ? (
              <>
                <p className="text-sm text-neutral-400 mb-8">
                  {getRejectedText(config.verificationType)}
                </p>
                {kybStatus?.rejectionReason && (
                  <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4 mb-6">
                    <div className="text-xs text-red-400 mb-1">
                      Rejection Reason
                    </div>
                    <div className="text-sm text-red-300">
                      {kybStatus.rejectionReason}
                    </div>
                  </div>
                )}
                {statusMessage}
              </>
            ) : needsAdditionalInfo ? (
              <>
                <p className="text-sm text-neutral-400 mb-8">
                  Additional information is required to complete your
                  verification. Click below to continue.
                </p>
                {kybStatus?.additionalInfoRequested && (
                  <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-xl p-4 mb-6">
                    <div className="text-xs text-yellow-400 mb-1">
                      Information Requested
                    </div>
                    <div className="text-sm text-yellow-300">
                      {kybStatus.additionalInfoRequested}
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    className={twMerge(
                      'bg-[#34FFC2]',
                      'flex',
                      'h-12',
                      'items-center',
                      'justify-center',
                      'rounded-xl',
                      'w-full',
                      'hover:bg-[#2FEDB3]',
                      'transition-colors',
                      'font-medium',
                      loading && 'opacity-50 cursor-not-allowed',
                    )}
                    onClick={() => initializeSumsubSdk()}
                    disabled={loading}
                  >
                    <div className="text-sm text-[#002365]">
                      {loading ? 'Loading...' : 'Continue Verification'}
                    </div>
                  </button>
                  {statusMessage}
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-neutral-400 mb-8">
                  {getDefaultText(config.verificationType, hasApplicant)}
                </p>
                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    className={twMerge(
                      'bg-[#34FFC2]',
                      'flex',
                      'h-12',
                      'items-center',
                      'justify-center',
                      'rounded-xl',
                      'w-full',
                      'hover:bg-[#2FEDB3]',
                      'transition-colors',
                      'font-medium',
                      loading && 'opacity-50 cursor-not-allowed',
                    )}
                    onClick={() => initializeSumsubSdk()}
                    disabled={loading}
                  >
                    <div className="text-sm text-[#002365]">
                      {loading
                        ? 'Loading...'
                        : hasApplicant
                          ? 'Continue Verification'
                          : 'Start Verification'}
                    </div>
                  </button>
                  {kybStatus?.status === 'NotStarted' && hasApplicant && (
                    <>
                      <p className="text-xs text-neutral-500">
                        If Sumsub says the verification expired or opens the
                        wrong flow, start a new verification instead.
                      </p>
                      <button
                        type="button"
                        className={twMerge(
                          'border border-neutral-700 text-neutral-200',
                          'flex',
                          'h-12',
                          'items-center',
                          'justify-center',
                          'rounded-xl',
                          'w-full',
                          'hover:border-neutral-500 hover:text-white',
                          'transition-colors',
                          loading && 'opacity-50 cursor-not-allowed',
                        )}
                        onClick={restartVerification}
                        disabled={loading}
                      >
                        <div className="text-sm">
                          {loading ? 'Loading...' : 'Start New Verification'}
                        </div>
                      </button>
                    </>
                  )}
                  {statusMessage}
                </div>
              </>
            )}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center mb-8">
              <motion.h1
                className="text-2xl font-semibold text-white mb-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.3 }}
              >
                {config.sdkTitle}
              </motion.h1>
              <motion.p
                className="text-sm text-neutral-400"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                Complete the verification process below
              </motion.p>
            </div>
            <motion.div
              id="sumsub-websdk-container"
              ref={sdkContainerRef}
              className="w-full bg-[#20252C] rounded-lg overflow-hidden [&>iframe]:transition-all [&>iframe]:duration-300 [&>iframe]:ease-out"
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{
                opacity: { duration: 0.3 },
                scale: {
                  duration: 0.3,
                  type: 'spring',
                  stiffness: 400,
                  damping: 30,
                },
                layout: {
                  duration: 0.4,
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                },
              }}
            />
            {error && <div className="mt-4 text-sm text-red-400">{error}</div>}
            {error && kybStatus?.status === 'NotStarted' && (
              <button
                type="button"
                className={twMerge(
                  'mt-4 border border-neutral-700 text-neutral-200',
                  'flex',
                  'h-12',
                  'items-center',
                  'justify-center',
                  'rounded-xl',
                  'w-full',
                  'hover:border-neutral-500 hover:text-white',
                  'transition-colors',
                  loading && 'opacity-50 cursor-not-allowed',
                )}
                onClick={restartVerification}
                disabled={loading}
              >
                <div className="text-sm">
                  {loading ? 'Loading...' : 'Start New Verification'}
                </div>
              </button>
            )}
            {!error && notice && (
              <div className="mt-4 text-sm text-yellow-300">{notice}</div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
